import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, Plus, Save, ThumbsDown, ThumbsUp } from 'lucide-react';
import {
  helpApi,
  HELP_ACCENTS,
  HelpAudience,
  HelpArticle,
  HelpCollection,
  UpsertHelpArticleDto,
  UpsertHelpCollectionDto,
} from '../../api/help';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { APP_LABEL, AppName, appsFor, screenAt, screensFor } from './appScreens';
import { ACCENT_STYLE, FALLBACK_ICON, HelpTile } from './HelpIcon';
import { IconPicker } from './IconPicker';
import { ScreenPicker } from './ScreenPicker';

/**
 * Authoring the mobile help centre.
 *
 * These articles used to be compiled into the app, which meant a wrong one
 * stayed wrong until the next release. This is where they change now.
 *
 * ## Nothing here deletes
 *
 * Archiving is the destructive operation and it is a flag. An article
 * somebody bookmarked, or that an agent pasted into a support conversation
 * last week, should stop being offered without starting to 404.
 *
 * ## One app at a time
 *
 * Both apps have a "Payments" and a "Cards", and in a single list they read
 * as duplicates — the way an article meant for business customers ends up
 * in the consumer app. So the list is per app, a collection written for both
 * says so, and whatever is being edited names the app it appears in.
 *
 * ## Why the body is a plain textarea
 *
 * The app renders one array entry per paragraph, and a leading `-` as a
 * bullet. It deliberately has no markdown or HTML renderer — that would put
 * an injection surface inside the screen whose whole job is to be
 * trustworthy when somebody is already worried about their money. So the
 * editor is one line per paragraph, which is exactly what the app stores.
 */

/** Lowercase words joined by hyphens — the same rule the API enforces. */
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const AUDIENCES: { value: HelpAudience; label: string; detail: string }[] = [
  { value: 'consumer', label: APP_LABEL.consumer, detail: 'Personal accounts only' },
  { value: 'business', label: APP_LABEL.business, detail: 'Business accounts only' },
  { value: 'all', label: 'Both apps', detail: 'The answer is the same in each' },
];

/** Where a collection shows, in words. */
function audienceLabel(audience: HelpAudience): string {
  return AUDIENCES.find((a) => a.value === audience)?.label ?? audience;
}

const APP_STORAGE_KEY = 'help-centre.app';

function storedApp(): AppName {
  try {
    return localStorage.getItem(APP_STORAGE_KEY) === 'business' ? 'business' : 'consumer';
  } catch {
    return 'consumer';
  }
}

interface ArticleDraft {
  slug: string;
  collectionSlug: string;
  title: string;
  summary: string;
  readMinutes: string;
  body: string;
  actionLabel: string;
  actionRoute: string;
  sortOrder: string;
  archived: boolean;
}

function draftFrom(
  article: HelpArticle | null,
  collectionSlug: string,
): ArticleDraft {
  return {
    slug: article?.slug ?? '',
    collectionSlug: article?.collection?.slug ?? collectionSlug,
    title: article?.title ?? '',
    summary: article?.summary ?? '',
    readMinutes: String(article?.readMinutes ?? 2),
    // One line per paragraph, which is how the app stores and renders them.
    body: (article?.body ?? []).join('\n'),
    actionLabel: article?.action?.label ?? '',
    actionRoute: article?.action?.route ?? '',
    sortOrder: String(article?.sortOrder ?? 0),
    archived: article?.archived ?? false,
  };
}

function toArticleDto(draft: ArticleDraft): UpsertHelpArticleDto {
  const label = draft.actionLabel.trim();
  const route = draft.actionRoute.trim();
  return {
    slug: draft.slug.trim(),
    collectionSlug: draft.collectionSlug,
    title: draft.title.trim(),
    summary: draft.summary.trim(),
    readMinutes: Number(draft.readMinutes) || 1,
    body: draft.body
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
    // Both halves or neither: a label with no route is a button that does
    // nothing, and a route with no label is one nobody can see.
    action: label && route ? { label, route } : null,
    sortOrder: Number(draft.sortOrder) || 0,
    archived: draft.archived,
  };
}

/** The first thing wrong with a draft, or null. */
function articleProblem(draft: ArticleDraft): string | null {
  if (!SLUG.test(draft.slug.trim())) {
    return 'Slug must be lowercase words joined by hyphens.';
  }
  if (!draft.title.trim()) return 'A title is required.';
  if (!draft.summary.trim()) return 'A summary is required.';
  if (!draft.body.trim()) return 'An article with no body helps nobody.';
  const label = draft.actionLabel.trim();
  const route = draft.actionRoute.trim();
  if ((label && !route) || (route && !label)) {
    return 'An action needs both a label and a route, or neither.';
  }
  // An article body is editable text the app hands to its router, so an
  // external URL here would be an open redirect with an official-looking
  // button on it. The API refuses these too; catching it in the editor means
  // the person who typed it finds out.
  if (route && !route.startsWith('/')) {
    return 'Route must be an in-app path beginning with "/".';
  }
  // The apps hide a button whose route is not on their list, so saving one
  // would publish an article whose action quietly never appears.
  if (route && !screenAt(route)) {
    return 'Pick the action’s screen from the list — the app will not show a button for that route.';
  }
  return null;
}

export const HelpCentre: React.FC = () => {
  usePage('Help Centre');
  const toast = useToast();

  const [collections, setCollections] = useState<HelpCollection[]>([]);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string>('');
  const [app, setApp] = useState<AppName>(storedApp);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [articleDraft, setArticleDraft] = useState<ArticleDraft | null>(null);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [collectionDraft, setCollectionDraft] =
    useState<UpsertHelpCollectionDto | null>(null);
  const [editingCollection, setEditingCollection] = useState<string | null>(
    null,
  );

  /** The collections the chosen app shows — its own, and those for both. */
  const appCollections = useMemo(
    () => collections.filter((c) => appsFor(c.audience).includes(app)),
    [collections, app],
  );

  // Falls back to the app's first collection, so switching app never lands
  // on one the other app shows.
  const selected = useMemo(
    () => appCollections.find((c) => c.slug === selectedSlug) ?? appCollections[0],
    [appCollections, selectedSlug],
  );

  function chooseApp(next: AppName) {
    setApp(next);
    try {
      localStorage.setItem(APP_STORAGE_KEY, next);
    } catch {
      // A preference, not state: the default is fine.
    }
  }

  /** The collection the article being edited belongs to. */
  const articleCollection = useMemo(
    () => collections.find((c) => c.slug === articleDraft?.collectionSlug),
    [collections, articleDraft?.collectionSlug],
  );

  /** Which app the article being edited appears in, from its collection —
   *  it decides which screens its button can open. */
  const articleAudience = articleCollection?.audience;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cols, arts] = await Promise.all([
        helpApi.listCollections(),
        helpApi.listArticles(),
      ]);
      setCollections(cols);
      setArticles(arts);
    } catch (err) {
      toast.error(err, 'Could not load the help centre.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleArticles = useMemo(
    () => articles.filter((a) => a.collection?.slug === selected?.slug),
    [articles, selected],
  );

  async function saveArticle() {
    if (!articleDraft) return;
    const problem = articleProblem(articleDraft);
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    try {
      const dto = toArticleDto(articleDraft);
      if (editingSlug) {
        await helpApi.updateArticle(editingSlug, dto);
        toast.success('Article updated.');
      } else {
        await helpApi.createArticle(dto);
        toast.success('Article created.');
      }
      setArticleDraft(null);
      setEditingSlug(null);
      await load();
    } catch (err) {
      toast.error(err, 'Could not save the article.');
    } finally {
      setSaving(false);
    }
  }

  async function saveCollection() {
    if (!collectionDraft) return;
    const slug = (collectionDraft.slug ?? '').trim();
    if (!SLUG.test(slug)) {
      toast.error('Slug must be lowercase words joined by hyphens.');
      return;
    }
    const label = collectionDraft.label?.trim() ?? '';
    if (!label) {
      toast.error('A label is required.');
      return;
    }
    // Two "Payments" in one app is two tiles nobody can tell apart.
    const apps = appsFor(collectionDraft.audience);
    const clash = collections.find(
      (c) =>
        c.slug !== (editingCollection ?? slug) &&
        !c.archived &&
        c.label.trim().toLowerCase() === label.toLowerCase() &&
        appsFor(c.audience).some((a) => apps.includes(a)),
    );
    if (clash) {
      const shared = appsFor(clash.audience).filter((a) => apps.includes(a));
      toast.error(
        `The ${shared.map((a) => APP_LABEL[a]).join(' and the ')} already ${
          shared.length > 1 ? 'have' : 'has'
        } a “${clash.label}” collection.`,
      );
      return;
    }
    setSaving(true);
    try {
      if (editingCollection) {
        await helpApi.updateCollection(editingCollection, collectionDraft);
        toast.success('Collection updated.');
      } else {
        await helpApi.createCollection({
          icon: FALLBACK_ICON,
          accent: HELP_ACCENTS[0],
          ...collectionDraft,
          slug,
        });
        toast.success('Collection created.');
      }
      setCollectionDraft(null);
      setEditingCollection(null);
      await load();
    } catch (err) {
      toast.error(err, 'Could not save the collection.');
    } finally {
      setSaving(false);
    }
  }

  /** Archive and restore are the same call — the flag just flips. */
  async function toggleArchive(article: HelpArticle) {
    try {
      await helpApi.updateArticle(article.slug, { archived: !article.archived });
      toast.success(article.archived ? 'Article restored.' : 'Article archived.');
      await load();
    } catch (err) {
      toast.error(err, 'Could not change the article.');
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(220px, 280px) 1fr',
        gap: 24,
        alignItems: 'start',
      }}
    >
      <Card padding={0}>
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid var(--neutral-300)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div>
            <h3
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--neutral-800)',
              }}
            >
              Collections
            </h3>
            <p
              style={{
                marginTop: 4,
                fontSize: 12,
                color: 'var(--neutral-500)',
              }}
            >
              Topics in each app's help centre.
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`New collection in the ${APP_LABEL[app]}`}
            onClick={() => {
              setEditingCollection(null);
              setCollectionDraft({
                audience: app,
                icon: FALLBACK_ICON,
                accent: HELP_ACCENTS[0],
                sortOrder: appCollections.length,
              });
            }}
          >
            <Plus size={15} />
          </Button>
        </div>
        <div
          role="tablist"
          aria-label="App"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 4,
            margin: '12px 12px 4px',
            padding: 4,
            background: 'var(--neutral-200)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {(['consumer', 'business'] as AppName[]).map((option) => {
            const on = option === app;
            return (
              <button
                key={option}
                role="tab"
                aria-selected={on}
                onClick={() => chooseApp(option)}
                style={{
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: '7px 8px',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  background: on ? 'var(--neutral-100)' : 'transparent',
                  color: on ? 'var(--neutral-800)' : 'var(--neutral-600)',
                  boxShadow: on ? 'var(--shadow-sm)' : 'none',
                }}
              >
                {option === 'consumer' ? 'PayIQ' : 'PayIQ Business'}
              </button>
            );
          })}
        </div>
        <div style={{ padding: 8, display: 'grid', gap: 4 }}>
          {appCollections.length === 0 ? (
            <p style={{ padding: '12px 8px', fontSize: 13, color: 'var(--neutral-500)' }}>
              No collections in the {APP_LABEL[app]} yet.
            </p>
          ) : null}
          {appCollections.map((collection) => {
            const active = collection.slug === selected?.slug;
            const count = articles.filter(
              (a) => a.collection?.slug === collection.slug && !a.archived,
            ).length;
            return (
              <button
                key={collection.id}
                onClick={() => setSelectedSlug(collection.slug)}
                style={{
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  padding: '8px 10px',
                  background: active ? 'var(--blue-100)' : 'transparent',
                  color: active ? 'var(--blue-800)' : 'var(--neutral-700)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  opacity: collection.archived ? 0.5 : 1,
                }}
              >
                <HelpTile icon={collection.icon} accent={collection.accent} size={28} />
                <span style={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {collection.label}
                  {collection.audience === 'all' ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'var(--neutral-500)',
                      }}
                    >
                      Both apps
                    </span>
                  ) : null}
                </span>
                <span style={{ fontSize: 11, color: 'var(--neutral-500)' }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      <Card>
        {!selected ? (
          <EmptyState
            title="No collections yet"
            description="Create one to start writing help articles."
          />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0 }}>
                <HelpTile icon={selected.icon} accent={selected.accent} size={44} />
                <div style={{ minWidth: 0 }}>
                  <h2
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      color: 'var(--neutral-800)',
                    }}
                  >
                    {selected.label}
                  </h2>
                  <p
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: 'var(--neutral-500)',
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <AppBadge audience={selected.audience} />
                    {selected.slug}
                    {selected.archived ? <Badge variant="neutral">Archived</Badge> : null}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditingCollection(selected.slug);
                    setCollectionDraft({
                      slug: selected.slug,
                      label: selected.label,
                      icon: selected.icon,
                      accent: selected.accent,
                      audience: selected.audience,
                      sortOrder: selected.sortOrder,
                      archived: selected.archived,
                    });
                  }}
                >
                  Edit collection
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingSlug(null);
                    setArticleDraft(draftFrom(null, selected.slug));
                  }}
                >
                  <Plus size={15} /> New article
                </Button>
              </div>
            </div>

            <div style={{ marginTop: 20, display: 'grid', gap: 10 }}>
              {visibleArticles.length === 0 ? (
                <EmptyState
                  title="No articles in this topic"
                  description="Write the first one."
                />
              ) : (
                visibleArticles.map((article) => (
                  <div
                    key={article.id}
                    style={{
                      border: '1px solid var(--neutral-300)',
                      borderRadius: 'var(--radius-md)',
                      padding: 14,
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      opacity: article.archived ? 0.55 : 1,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        <strong
                          style={{
                            fontSize: 14,
                            color: 'var(--neutral-800)',
                          }}
                        >
                          {article.title}
                        </strong>
                        {article.archived ? (
                          <Badge variant="neutral">Archived</Badge>
                        ) : null}
                        {article.feedback ? (
                          <FeedbackTally {...article.feedback} />
                        ) : null}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--neutral-600)',
                          marginBottom: 6,
                        }}
                      >
                        {article.summary}
                      </div>
                      <div
                        style={{ fontSize: 11, color: 'var(--neutral-500)' }}
                      >
                        {article.slug} · {article.readMinutes} min ·{' '}
                        {article.body.length} paragraph
                        {article.body.length === 1 ? '' : 's'}
                        {article.action
                          ? ` · → ${screenAt(article.action.route)?.name ?? article.action.route}`
                          : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEditingSlug(article.slug);
                          setArticleDraft(draftFrom(article, selected.slug));
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void toggleArchive(article)}
                      >
                        <Archive size={14} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </Card>

      <Modal
        isOpen={!!articleDraft}
        onClose={() => {
          setArticleDraft(null);
          setEditingSlug(null);
        }}
        title={editingSlug ? 'Edit article' : 'New article'}
        maxWidth={720}
      >
        {articleDraft ? (
          <div style={{ display: 'grid', gap: 14 }}>
            {articleCollection ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  background: 'var(--neutral-200)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 13,
                  color: 'var(--neutral-700)',
                }}
              >
                <HelpTile icon={articleCollection.icon} accent={articleCollection.accent} size={28} />
                <span style={{ flex: 1 }}>
                  In <strong>{articleCollection.label}</strong>, shown in{' '}
                  {articleCollection.audience === 'all'
                    ? 'both apps'
                    : `the ${APP_LABEL[articleCollection.audience]}`}
                  .
                </span>
              </div>
            ) : null}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <Input
                label="Title"
                value={articleDraft.title}
                onChange={(e) =>
                  setArticleDraft((d) =>
                    d
                      ? {
                          ...d,
                          title: e.target.value,
                          // Only while creating: changing a live slug breaks
                          // links people already hold.
                          slug: editingSlug ? d.slug : slugify(e.target.value),
                        }
                      : d,
                  )
                }
              />
              <Input
                label="Slug"
                value={articleDraft.slug}
                onChange={(e) =>
                  setArticleDraft((d) =>
                    d ? { ...d, slug: e.target.value } : d,
                  )
                }
              />
            </div>

            <Input
              label="Summary — one line, what this answers"
              value={articleDraft.summary}
              onChange={(e) =>
                setArticleDraft((d) =>
                  d ? { ...d, summary: e.target.value } : d,
                )
              }
            />

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--neutral-700)',
                  marginBottom: 6,
                }}
              >
                Body — one paragraph per line, a leading “-” makes a bullet
              </label>
              <textarea
                value={articleDraft.body}
                onChange={(e) =>
                  setArticleDraft((d) =>
                    d ? { ...d, body: e.target.value } : d,
                  )
                }
                rows={12}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--neutral-300)',
                  fontSize: 13,
                  lineHeight: 1.6,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>

            <Input
              label="Action button text (optional)"
              placeholder={
                articleAudience === 'business' ? 'Open invoices' : 'Open security settings'
              }
              value={articleDraft.actionLabel}
              onChange={(e) =>
                setArticleDraft((d) =>
                  d ? { ...d, actionLabel: e.target.value } : d,
                )
              }
            />
            <ScreenPicker
              label="Action button opens"
              value={articleDraft.actionRoute}
              screens={screensFor(articleAudience)}
              hint={
                appsFor(articleAudience).length > 1
                  ? `This collection shows in both apps. The button only appears in the app that has the screen you pick.`
                  : `Screens in the ${APP_LABEL[appsFor(articleAudience)[0]]}.`
              }
              onChange={(route) =>
                setArticleDraft((d) => (d ? { ...d, actionRoute: route } : d))
              }
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <Input
                label="Read minutes"
                type="number"
                value={articleDraft.readMinutes}
                onChange={(e) =>
                  setArticleDraft((d) =>
                    d ? { ...d, readMinutes: e.target.value } : d,
                  )
                }
              />
              <Input
                label="Sort order"
                type="number"
                value={articleDraft.sortOrder}
                onChange={(e) =>
                  setArticleDraft((d) =>
                    d ? { ...d, sortOrder: e.target.value } : d,
                  )
                }
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                variant="secondary"
                onClick={() => {
                  setArticleDraft(null);
                  setEditingSlug(null);
                }}
              >
                Cancel
              </Button>
              <Button disabled={saving} onClick={() => void saveArticle()}>
                <Save size={15} /> {saving ? 'Saving…' : 'Save article'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={!!collectionDraft}
        onClose={() => {
          setCollectionDraft(null);
          setEditingCollection(null);
        }}
        title={editingCollection ? 'Edit collection' : 'New collection'}
        maxWidth={520}
      >
        {collectionDraft ? (
          <div style={{ display: 'grid', gap: 14 }}>
            <Input
              label="Label"
              value={collectionDraft.label ?? ''}
              onChange={(e) =>
                setCollectionDraft((d) =>
                  d
                    ? {
                        ...d,
                        label: e.target.value,
                        slug: editingCollection
                          ? d.slug
                          : slugify(e.target.value),
                      }
                    : d,
                )
              }
            />
            <Input
              label="Slug"
              value={collectionDraft.slug ?? ''}
              onChange={(e) =>
                setCollectionDraft((d) =>
                  d ? { ...d, slug: e.target.value } : d,
                )
              }
            />

            <AudiencePicker
              value={collectionDraft.audience ?? app}
              onChange={(audience) =>
                setCollectionDraft((d) => (d ? { ...d, audience } : d))
              }
            />

            <IconPicker
              value={collectionDraft.icon ?? FALLBACK_ICON}
              accent={collectionDraft.accent ?? HELP_ACCENTS[0]}
              onChange={(icon) => setCollectionDraft((d) => (d ? { ...d, icon } : d))}
            />

            <AccentPicker
              value={collectionDraft.accent ?? HELP_ACCENTS[0]}
              icon={collectionDraft.icon ?? FALLBACK_ICON}
              onChange={(accent) => setCollectionDraft((d) => (d ? { ...d, accent } : d))}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                variant="secondary"
                onClick={() => {
                  setCollectionDraft(null);
                  setEditingCollection(null);
                }}
              >
                Cancel
              </Button>
              <Button disabled={saving} onClick={() => void saveCollection()}>
                <Save size={15} /> {saving ? 'Saving…' : 'Save collection'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--neutral-700)',
  marginBottom: 6,
};

/** Which app a collection shows in — named, never the raw enum. */
const AppBadge: React.FC<{ audience: HelpAudience }> = ({ audience }) => (
  <span
    style={{
      display: 'inline-flex',
      padding: '2px 8px',
      borderRadius: 'var(--radius-full)',
      fontSize: 11,
      fontWeight: 600,
      background: audience === 'business' ? 'var(--neutral-800)' : 'var(--blue-100)',
      color: audience === 'business' ? 'var(--neutral-100)' : 'var(--blue-800)',
    }}
  >
    {audienceLabel(audience)}
  </span>
);

/**
 * Where a collection shows. Radio cards rather than a select of
 * `business / consumer / all`, because this is the choice that puts an
 * article in front of the wrong customers.
 */
const AudiencePicker: React.FC<{
  value: HelpAudience;
  onChange: (audience: HelpAudience) => void;
}> = ({ value, onChange }) => (
  <div role="radiogroup" aria-label="Shown in">
    <span style={fieldLabel}>Shown in</span>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
      {AUDIENCES.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(option.value)}
            style={{
              textAlign: 'left',
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              border: `1px solid ${on ? 'var(--blue-700)' : 'var(--neutral-300)'}`,
              background: on ? 'var(--blue-100)' : 'var(--neutral-100)',
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)' }}>
              {option.label}
            </div>
            <div style={{ marginTop: 2, fontSize: 11, color: 'var(--neutral-500)' }}>
              {option.detail}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

/** The accent roles, each shown on the collection's own icon. */
const AccentPicker: React.FC<{
  value: string;
  icon: string;
  onChange: (accent: string) => void;
}> = ({ value, icon, onChange }) => (
  <div role="radiogroup" aria-label="Accent">
    <span style={fieldLabel}>Accent</span>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {HELP_ACCENTS.map((accent) => {
        const on = accent === value;
        return (
          <button
            key={accent}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={accent}
            title={accent}
            onClick={() => onChange(accent)}
            style={{
              padding: 3,
              borderRadius: 'var(--radius-md)',
              border: `2px solid ${on ? ACCENT_STYLE[accent].color : 'transparent'}`,
              background: 'transparent',
              cursor: 'pointer',
              display: 'inline-flex',
            }}
          >
            <HelpTile icon={icon} accent={accent} size={34} />
          </button>
        );
      })}
    </div>
  </div>
);

/**
 * Yes and No from "Did this answer your question?", beside the title.
 *
 * Amber when more people said No than Yes: that is the article to rewrite
 * first, and it should stand out in a list of forty. Nothing at all before
 * anybody has answered, rather than a row of zeros.
 */
function FeedbackTally({ yes, no }: { yes: number; no: number }) {
  if (yes + no === 0) return null;
  const struggling = no > yes;
  return (
    <span
      title={`${yes} said it answered their question, ${no} said it didn't`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 999,
        background: struggling ? 'var(--warning-100)' : 'var(--neutral-100)',
        color: struggling ? 'var(--warning-900)' : 'var(--neutral-700)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <ThumbsUp size={12} /> {yes}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <ThumbsDown size={12} /> {no}
      </span>
    </span>
  );
}
